import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FLOWS } from '../data/flows';
import '../ds/tokens/fonts.css';
import '../ds/tokens/tokens.css';
import '../ds/styles/components.css';
import '../guia-home.css';
import Button from '../ds/components/Button';
import LoadingDots from '../ds/components/LoadingDots';
import Brujula, { Logo } from '../ds/components/Brujula';
import CierreHome from './CierreHome';
import AgenciaCelular from './AgenciaCelular';
import { PRODUCTOS, GRUPOS, PROMESA, SOY } from '../data/productos';
import ChatContacto from './ChatContacto';
import { evento, iniciarMedicion } from '../lib/medicion';
import { clasificar, RUBRO_LABEL, CONFIANZA_MINIMA } from '../lib/typesafe';


const MAX_STEPS = 7;

// ── Option chip ────────────────────────────────────────────────────�
// El celular de la apertura: las tres pantallas de Agencia rotan solas (catálogo, seguimiento, consulta).
const CelularQueRota = () => {
  const [paso, setPaso] = useState(0);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    const t = setInterval(() => setPaso(p => (p + 1) % 3), 4500);
    return () => clearInterval(t);
  }, []);
  return <AgenciaCelular paso={paso} onIr={setPaso} />;
};

const Chip = ({ label, onClick, disabled, index, base = 350 }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="gh-opcion gh-chip-entra"
    style={{ '--d': `${base + index * 60}ms` }}
  >
    {label}
  </button>
);

// ── Text input ─────────────────────────────────────────────────────
const TextInput = ({ placeholder, etiqueta, onSubmit, disabled, allowEmpty }) => {
  const [val, setVal] = useState('');
  const ref = useRef(null);

  // El foco automático solo con puntero fino: en el teléfono abriría el teclado sin que lo pidan.
  useEffect(() => {
    const fino = window.matchMedia?.('(pointer: fine)').matches;
    if (fino && !disabled && ref.current) ref.current.focus();
  }, [disabled]);

  const submit = (e) => {
    e.preventDefault();
    if (val.trim() || allowEmpty) { onSubmit(val.trim()); setVal(''); }
  };

  return (
    <form
      onSubmit={submit}
      className="gh-campo"
      style={{ animation: 'fadeSlideUp 0.35s ease-out 0.1s both' }}
    >
      <input
        ref={ref}
        type="text"
        value={val}
        onChange={e => setVal(e.target.value)}
        placeholder={placeholder || 'Escribí tu respuesta…'}
        aria-label={etiqueta || placeholder || 'Tu respuesta'}
        name="respuesta"
        autoComplete="off"
        disabled={disabled}
        className="gh-input"
      />
      <Button
        variant="solido"
        type="submit"
        disabled={disabled || (!val.trim() && !allowEmpty)}
      >
        Enviar
      </Button>
    </form>
  );
};

// Todo resultado es la página final (CierreHome): valor, oferta del producto y contacto.
const ResultExtras = ({ node, onRestart, onNavigate, collected, abrirChat }) => (
  <CierreHome node={node} collected={collected} abrirChat={abrirChat} onNavigate={onNavigate} onRestart={onRestart} />
);

// ── Main ──────────────────────────────────────────────────────────────────
const ConversationalExperience = () => {
  const navigate = useNavigate();
  const [phase, setPhase]             = useState('landing');
  const [currentNode, setCurrentNode] = useState(null);
  const [displayText, setDisplayText] = useState('');
  const [fullText, setFullText]       = useState('');   // el mensaje entero: reserva el lugar de lo que todavía no se dijo
  const mensajeRef = useRef(null);
  const [frozen, setFrozen]           = useState(false);
  const [step, setStep]               = useState(0);
  const [visible, setVisible]         = useState(true);
  const [chatAbierto, setChatAbierto] = useState(false);
  const [chatTexto, setChatTexto]     = useState('');
  // El cierre abre este mismo chat con el asunto escrito y los datos del recorrido (An, 04/11: «tiene que abrirse
  // en el diálogo con el asunto de inscripción; ¿por qué harías otro?»).
  const [chatContexto, setChatContexto] = useState(null);
  const abrirChat = (texto, contexto = null) => { setChatTexto(texto); setChatContexto(contexto); setChatAbierto(true); };
  const [collected, setCollected]     = useState({});
  const typingRef = useRef(null);
  // Sube con cada reinicio: una clasificación que vuelve tarde no se mezcla
  // con la conversación siguiente.
  const conversacionRef = useRef(0);

  // Word-by-word typewriter — feels like thinking, not printing
  const typewrite = (text, onDone) => {
    const parts = text.split(' ');
    let i = 0;
    setFullText(text);
    setDisplayText('');
    clearInterval(typingRef.current);
    typingRef.current = setInterval(() => {
      i++;
      setDisplayText(parts.slice(0, i).join(' '));
      if (i >= parts.length) {
        clearInterval(typingRef.current);
        onDone && onDone();
      }
    }, 60);
  };

  const transition = (fn) => {
    setVisible(false);
    setTimeout(() => { fn(); setVisible(true); }, 520);
  };

  // Pasa al nodo siguiente y lo escribe. La escritura arranca cuando la transición ya cambió de nodo:
  // con un reloj aparte, un mensaje de dos palabras («Contanos cuál.») terminaba antes que la
  // transición, que lo volvía a «escribiendo» y dejaba la pantalla trabada.
  // Si la persona reinició durante la transición, el nodo viejo no vuelve.
  const decir = (next, collectedOverride) => {
    const conversacion = conversacionRef.current;
    transition(() => {
      if (conversacionRef.current !== conversacion) return;
      setPhase('typing'); setDisplayText(''); setCurrentNode(next);
      // La página final aparece entera, sin escribirse palabra por palabra (An, 04/11: «la animación de la
      // propuesta es un montón»). Las preguntas siguen escribiéndose.
      if (next.isResult) {
        const texto = buildMessage(next, collectedOverride);
        clearInterval(typingRef.current); setFullText(texto); setDisplayText(texto);
        setPhase('result'); setFrozen(false);
        return;
      }
      typewrite(buildMessage(next, collectedOverride), () => {
        setPhase(next.isResult ? 'result' : 'question');
        setFrozen(false);
      });
    });
  };

  // Inject name into result messages if we have it
  const buildMessage = (node, collectedOverride) => {
    const c = collectedOverride ?? collected;
    let msg = c.para === 'personal' && node.mensajePersonal ? node.mensajePersonal : node.message;
    if (node.isResult && c.name) {
      msg = `Hola, ${c.name}.\n\n${msg}`;
    }
    return msg;
  };

  // La landing ya es el primer turno de la charla: elegir una opción de acá
  // es contestar «¿Qué te trae?» sin pasar por un botón de arranque.
  // Las opciones son los productos (spec v4): el producto elegido queda en `producto`,
  // que el cierre usa para destacarlo y que viaja con el contacto.
  const handleEleccionInicial = (opt, fija) => {
    setStep(1);
    const nuevo = { ...collected, producto: opt.id, ...fija };
    if (opt.id) setCollected(nuevo);
    handleOption(opt, nuevo);
  };
  // «Soy: Empresa / Persona» (An, 05/11): un switch arriba de los productos; lo elegido viaja como `para`.
  const [soy, setSoy] = useState('empresa');
  const elegirSoy = (id) => {
    if (id === soy) return;
    evento('respuesta', { pantalla: 'soy', opcion: id });
    setSoy(id);
  };
  const elegirProducto = (p) => {
    const fija = soy === 'personal' ? { para: 'personal', ...p.personal } : { para: 'empresa' };
    handleEleccionInicial({ id: p.id, label: p.nombre, next: p.next }, fija);
  };

  const handleOption = (opt, collectedOverride) => {
    if (frozen) return;
    // Una opción sin recorrido propio abre el chat con su pedido escrito; la pregunta queda en pantalla.
    if (opt.chat) {
      if (opt.id) evento('respuesta', { pantalla: currentNode?.id || 'start', opcion: opt.id });
      setChatTexto(opt.chat); setChatAbierto(true);
      return;
    }
    setFrozen(true);
    const next = FLOWS[opt.next];
    if (!next) return;
    // Solo el id de la opción: nunca el texto (spec v4, experimento de lenguaje de ia_tarea).
    if (opt.id) evento('respuesta', { pantalla: currentNode?.id || 'start', opcion: opt.id });
    setStep(s => Math.min(s + 1, MAX_STEPS));

    if (currentNode?.dataKey) {
      setCollected(prev => ({ ...prev, [currentNode.dataKey]: opt.label }));
    }
    // `fija`: datos que la opción deja puestos sin preguntarlos (el camino «Para mí»).
    if (opt.fija) setCollected(prev => ({ ...prev, ...opt.fija }));

    decir(next, collectedOverride);
  };

  const handleText = (text) => {
    if (frozen) return;
    if (!text.trim() && !currentNode?.skippable) return;
    if (!currentNode?.next) return;
    setFrozen(true);
    const next = FLOWS[currentNode.next];
    if (!next) return;
    setStep(s => Math.min(s + 1, MAX_STEPS));

    let newCollected = collected;
    if (currentNode?.dataKey) {
      newCollected = { ...collected, [currentNode.dataKey]: text.trim() };
      setCollected(newCollected);
    }

    // Corre en paralelo mientras la persona contesta lo que sigue.
    const conversacion = conversacionRef.current;
    const guardar = dato => {
      if (conversacionRef.current === conversacion) setCollected(prev => ({ ...prev, ...dato }));
    };
    if (currentNode?.dataKey === 'businessInfo' && text.trim()) {
      clasificar('rubro', text).then(r => {
        if (!r) return;
        const rubro = r.confidence >= CONFIANZA_MINIMA && RUBRO_LABEL[r.choice];
        // rubroSugerido viaja siempre (aunque no pre-llene) para registrar
        // después qué dejó la persona en WebChat.
        guardar({
          rubroSugerido: {
            valor: RUBRO_LABEL[r.choice] || 'Otro',
            confianza: r.confidence,
            // La ganadora muestra la misma confianza que decide completar o
            // preguntar; si no, su barra podría cruzar la línea del 70% en un
            // caso donde igual se preguntó.
            opciones: (r.top || []).map(o => ({
              valor: RUBRO_LABEL[o.choice] || 'Otro',
              p: o.choice === r.choice ? r.confidence : o.p,
            })),
          },
          ...(rubro ? { rubro } : {}),
        });
      });
    }
    decir(next, newCollected);
  };

  const handleNavigate = () => {
    if (!currentNode?.navTo) return;
    navigate(currentNode.navTo, {
      state: { type: currentNode.navType, dest: currentNode.navDest, passState: collected },
    });
  };

  const handleRestart = () => {
    clearInterval(typingRef.current);
    conversacionRef.current += 1;
    setPhase('landing');
    setCurrentNode(null);
    setDisplayText('');
    setFrozen(false);
    setStep(0);
    setVisible(true);
    setCollected({});
    window.scrollTo(0, 0);
  };

  useEffect(() => () => clearInterval(typingRef.current), []);
  useEffect(() => iniciarMedicion(), []);

  // La brújula de la apertura recorre las soluciones y apunta a cada una, como una aguja (An, 04/11: «la brújula
  // debería moverse por el rango de altura de los items, como apuntando»; y después: «que la flecha apunte a las
  // soluciones, una animación de movimiento en el rango de opciones, como cualquier brújula»).
  // La punta se mide del dibujo (el punto del contorno más lejos del centro de giro), no se supone. Cada cambio
  // se pasa un poco y oscila hasta quedarse, y el botón señalado se marca. Quieta con reduced-motion u oculta.
  const brujulaRef = useRef(null);
  useEffect(() => {
    const aguja = brujulaRef.current;
    if (phase !== 'landing' || !aguja) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    let destinos = []; // { boton, giro }
    let actual = 0;
    const medir = () => {
      if (!aguja.offsetParent) { destinos = []; return; } // en el celular la brújula no se muestra
      const previo = aguja.style.transform;
      aguja.style.transform = 'none';
      const r = aguja.getBoundingClientRect();
      const [ox, oy] = [r.left + r.width / 2, r.top + r.height * 0.55]; // transform-origin del CSS
      // La punta: el punto del contorno del símbolo más lejos del centro de giro.
      let punta = [r.right, oy];
      let lejos = 0;
      aguja.querySelectorAll('path, polygon').forEach(forma => {
        if (!forma.getTotalLength) return;
        const largo = forma.getTotalLength();
        const m = forma.getScreenCTM();
        for (let k = 0; k <= 120; k += 1) {
          const q = forma.getPointAtLength((largo * k) / 120);
          const x = m.a * q.x + m.c * q.y + m.e;
          const y = m.b * q.x + m.d * q.y + m.f;
          const d = Math.hypot(x - ox, y - oy);
          if (d > lejos) { lejos = d; punta = [x, y]; }
        }
      });
      aguja.style.transform = previo;
      const grados = ([x, y]) => (Math.atan2(y - oy, x - ox) * 180) / Math.PI;
      const reposo = grados(punta);
      destinos = [...aguja.parentElement.querySelectorAll('.gh-boton')].map(boton => {
        const b = boton.getBoundingClientRect();
        return { boton, giro: grados([b.left + 12, b.top + b.height / 2]) - reposo };
      });
    };
    medir();
    let i = 0;
    let animacion = null;
    const apuntar = () => {
      if (!destinos.length) return;
      const { boton, giro } = destinos[i % destinos.length];
      const paso = giro - actual;
      destinos.forEach(d => d.boton.classList.toggle('gh-boton--senalado', d.boton === boton));
      animacion?.cancel();
      // Como una aguja: se pasa, vuelve, y se asienta.
      animacion = aguja.animate([
        { transform: `rotate(${actual}deg)` },
        { transform: `rotate(${giro + paso * 0.18}deg)`, offset: 0.45 },
        { transform: `rotate(${giro - paso * 0.07}deg)`, offset: 0.7 },
        { transform: `rotate(${giro + paso * 0.025}deg)`, offset: 0.87 },
        { transform: `rotate(${giro}deg)` },
      ], { duration: 1500, easing: 'ease-out', fill: 'forwards' });
      actual = giro;
      i += 1;
    };
    apuntar();
    const reloj = setInterval(apuntar, 2400);
    const observador = new ResizeObserver(medir); // re-mide si cambia la grilla (ancho, fuentes, celular→escritorio)
    observador.observe(aguja.parentElement);
    return () => {
      clearInterval(reloj); observador.disconnect(); animacion?.cancel();
      destinos.forEach(d => d.boton.classList.remove('gh-boton--senalado'));
    };
  }, [phase]);
  // Cada pantalla de la charla se mide por su id; nunca por lo que la persona escribió.
  useEffect(() => {
    if (phase === 'question' || phase === 'result') evento('paso_visto', { pantalla: currentNode?.id });
  }, [phase, currentNode?.id]);

  // Cada pantalla nueva lleva el foco a su mensaje (para teclado y lector de pantalla)
  useEffect(() => {
    if (phase === 'question' || phase === 'result') mensajeRef.current?.focus({ preventScroll: true });
  }, [phase, currentNode?.id]);

  return (
    <div className="gh-root" data-theme="claro">
      {/* Header */}
      <header className="gh-header">
        <button
          onClick={step > 0 ? handleRestart : undefined}
          className="gh-logo"
          data-activo={step > 0}
          tabIndex={step > 0 ? 0 : -1}
          aria-label={step > 0 ? 'GUIA: empezar de nuevo' : 'GUIA'}
        >
          <Brujula sobreClaro />
        </button>

        <div className="gh-header__lado">
          {/* En la página de un producto, la vuelta a los productos queda arriba y a la vista (sin preguntas antes, los
              puntos de avance ya no medían nada). */}
          {step > 0 && <span className="gh-pildora gh-pildora--volver"><button type="button" className="gh-link gh-volver" onClick={handleRestart}>← Productos</button></span>}
          {/* Las tareas (el catálogo), a mano desde todas las pantallas: es la demostración del producto (An, 05/11) */}
          <span className="gh-pildora gh-pildora--tareas"><Link to="/catalogo" className="gh-link" onClick={() => evento('respuesta', { pantalla: 'ir_catalogo', opcion: step > 0 ? collected.producto || 'pagina' : 'cabecera' })}>Ver las tareas</Link></span>
          {/* La puerta a quién está atrás de esto. */}
          <span className="gh-pildora gh-pildora--quien"><a href="/antonela" className="gh-link"><span className="gh-link__largo">¿Quién está detrás de todo esto?</span><span className="gh-link__corto">¿Quién está detrás?</span></a></span>
        </div>
      </header>

      {/* Stage */}
      <main className="gh-escena">
        <div className="gh-panel" data-fase={phase === 'landing' ? 'landing' : 'charla'} >

        {/* Landing: la frase es la composición; la charla ya empezó, y sube como el agua */}
        {phase === 'landing' && (
          <div
            className="gh-landing"
            style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.3s ease' }}
          >
            <div className="gh-inicio">
              {/* La muestra va siempre arriba (An, 04/11: «la muestra tiene que ir siempre arriba, para que den ganas de
                  seguir y de ver; el sistema andando es crucial»): el celular de Agencia a la izquierda, con la brújula
                  en su borde apuntando a los productos. Dos columnas, no tres (An: «no repartir en 3 chorizos»). */}
              <div className="gh-inicio__muestra">
                <div className="gh-inicio__celular"><CelularQueRota /></div>
                <Link to="/catalogo" className="gh-inicio__tareas" onClick={() => evento('respuesta', { pantalla: 'ir_catalogo', opcion: 'celular' })}>Ver todas las tareas →</Link>
                <div ref={brujulaRef} className="gh-inicio__brujula" aria-hidden="true"><Logo variante="simbolo" /></div>
              </div>
              <div className="gh-inicio__texto">
                <p className="gh-inicio__rotulo">{PROMESA.rotulo[soy]}</p>
                <h1 className="gh-inicio__titular">{PROMESA.titular}</h1>
                <p className="gh-inicio__bajada">{PROMESA.bajada}</p>
                <div className="gh-soy" role="radiogroup" aria-label="Soy">
                  <span className="gh-soy__rotulo" aria-hidden="true">Soy</span>
                  {SOY.map(o => (
                    <button key={o.id} type="button" role="radio" aria-checked={soy === o.id} disabled={frozen} onClick={() => elegirSoy(o.id)}>{o.label}</button>
                  ))}
                </div>
                {GRUPOS.map(g => (
                  <section key={g.id} className="gh-grupo" data-grupo={g.id} aria-labelledby={`gh-g-${g.id}`}>
                    <h2 id={`gh-g-${g.id}`} className="gh-grupo__titulo">{g.titulo}</h2>
                    {g.bajada && <p className="gh-grupo__bajada">{g.bajada}</p>}
                    <div className="gh-grupo__botones">
                      {PRODUCTOS.filter(p => p.grupo === g.id && !(p.soloEmpresa && soy === 'personal')).map(p => (
                        <button key={p.id} type="button" className={`gh-boton${p.id === 'ia' ? ' gh-boton--principal' : ''}`} disabled={frozen} onClick={() => elegirProducto(p)}>
                          <b>{p.nombre}</b><span>{p.linea}</span>
                        </button>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Typing / Question / Result — unified block, same font size */}
        {(phase === 'typing' || phase === 'question' || phase === 'result') && (
          <div
            className="gh-bloque"
            style={{
              opacity: visible ? 1 : 0,
              filter: visible ? 'blur(0px)' : 'blur(3px)',
              transform: visible ? 'translateY(0)' : 'translateY(12px)',
              transition: 'opacity 0.3s ease, transform 0.3s ease, filter 0.3s ease',
            }}
          >
            {/* Message — same size throughout typing AND result */}
            <div className="gh-texto" aria-hidden="true">
              {(fullText || displayText).split('\n\n').map((parte, i, todas) => {
                const antes = todas.slice(0, i).reduce((n, q) => n + q.length + 2, 0);
                const dicho = displayText.slice(antes, antes + parte.length);
                return (
                  <p key={i} className={i === 0 ? 'gh-mensaje' : 'gh-cuerpo'}>
                    {dicho}
                    {phase === 'typing' && displayText && dicho.length < parte.length && <span className="gh-cursor" />}
                    <span className="gh-resto">{parte.slice(dicho.length)}</span>
                  </p>
                );
              })}
            </div>
            {/* el mensaje entero, una sola vez y cuando termina de escribirse */}
            <div className="gh-sr" role="status" aria-live="polite" tabIndex={-1} ref={mensajeRef}>
              {phase === 'typing' ? null : displayText.split('\n\n').map((parte, i) => (i === 0 ? <h1 key={i}>{parte}</h1> : <p key={i}>{parte}</p>))}
            </div>

            {/* El silencio de antes de hablar, hecho visible */}
            {phase === 'typing' && !displayText && (
              <LoadingDots className="gh-pensando" size={9} />
            )}

            {/* Question options */}
            {phase === 'question' && (
              <div className="flex flex-col items-center gap-4 w-full">
                {currentNode?.input ? (
                  <>
                    <TextInput
                      placeholder={currentNode.placeholder}
                      etiqueta={currentNode.message}
                      onSubmit={handleText}
                      disabled={frozen}
                      allowEmpty={currentNode?.skippable}
                    />
                    {currentNode?.skippable && (
                      <button
                        onClick={() => handleText('')}
                        className="gh-sutil"
                        style={{ animation: 'fadeSlideUp 0.35s ease-out 0.2s both' }}
                      >
                        Continuar sin decirlo →
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <div className="gh-opciones">
                      {currentNode?.options?.map(o => (collected.para === 'personal' && o.labelPersonal ? { ...o, label: o.labelPersonal } : o)).map((opt, i) => (
                        <Chip
                          key={opt.label}
                          label={opt.label}
                          onClick={() => handleOption(opt)}
                          disabled={frozen}
                          index={i}
                        />
                      ))}
                    </div>
                  </>
                )}

                <button onClick={handleRestart} className="gh-sutil">
                  ← empezar de nuevo
                </button>
              </div>
            )}

            {/* Result extras — message stays above at large size */}
            {phase === 'result' && currentNode && (
              <ResultExtras
                node={currentNode}
                onRestart={handleRestart}
                onNavigate={handleNavigate}
                collected={collected}
                abrirChat={abrirChat}
              />
            )}
          </div>
        )}
        </div>

      </main>

      <ChatContacto abierto={chatAbierto} setAbierto={setChatAbierto} texto={chatTexto} contexto={chatContexto} />

      {/* Footer */}
      <footer className="gh-pie">
        <span>Córdoba, Argentina</span>
        <Link to="/privacidad">Privacidad</Link>
      </footer>
    </div>
  );
};

export default ConversationalExperience;
